import React, { useState } from "react";

import { defined } from "@formant/core/common/defined";
import { eventTypes } from "@formant/core/model/eventTypes";
import { IEventColumn } from "@formant/core/model/IEventColumn";
import { IStreamColumn } from "@formant/core/model/IStreamColumn";
import { ITableColumn } from "@formant/core/model/ITableColumn";
import { ITaskReportColumn } from "@formant/core/model/ITaskReportColumn";
import { IconName } from "@formant/ui/component/common/Icon";
import {
    IPickerGroup,
    IPickerTab,
    Picker,
    PickerItem,
} from "@formant/ui/component/picker";

import { analyticsStore } from "../../stores";
import { streamTypeIcons } from "../common/StreamIcon";
import { IItem } from "../ItemList/IItem";

export type AnalyticsPickerTabs = "streams" | "events" | "tasks";
export type AnalyticsGroup = string;
export type AnalyticsItem = PickerItem<AnalyticsPickerTabs, AnalyticsGroup>;

export interface INewAnalyticsPickerProps {
    displayType?: "popover" | "dropdown";
    type: AnalyticsPickerTabs | undefined;
    allowMultipleSelections?: boolean;
    selectedTableColumns?: ITableColumn[];
    deviceIds: string[];
    onSelectTableColumn: (tableColumn: ITableColumn, exists: boolean) => void;
    onSelectItem?: (item: AnalyticsItem) => void;
}

export function NewAnalyticsPicker(props: INewAnalyticsPickerProps) {
    const {
        type,
        selectedTableColumns,
        onSelectTableColumn,
        deviceIds,
        onSelectItem,
        displayType,
    } = props;

    const [selectedTab, setSelectedTab] = useState<
        AnalyticsPickerTabs | undefined
    >(type);

    const toStream = (tableColumn: ITableColumn): IStreamColumn => {
        return tableColumn as IStreamColumn;
    };

    const toTaskReport = (tableColumn: ITableColumn): ITaskReportColumn => {
        return tableColumn as ITaskReportColumn;
    };

    const toEvent = (tableColumn: ITableColumn): IEventColumn => {
        return tableColumn as IEventColumn;
    };

    const listStreams = (): AnalyticsItem[] => {
        const streamColumns =
            analyticsStore.listFilteredStreams({ deviceIds }) || [];

        return streamColumns.map(streamColumn => {
            const selected = (selectedTableColumns || [])
                .filter(_ => _.type === "stream")
                .map(_ => toStream(_).streamName)
                .includes(streamColumn.streamName);
            return {
                tab: "streams",
                group: streamColumn.streamType,
                icon: streamTypeIcons[streamColumn.streamType],
                label: streamColumn.streamName,
                toggled: selected,
                disabled: selected,
                checked: selected,
                onClick: () => {
                    onSelectTableColumn(streamColumn, selected);
                },
            };
        });
    };

    const listTaskReports = (): IItem[] => {
        const selectedTasks = (selectedTableColumns || [])
            .filter(_ => _.type === "task-report")
            .map(_ => toTaskReport(_));
        return (analyticsStore.listTaskReports({ deviceIds }) || [])
            .filter(_ =>
                selectedTasks?.[0]?.tableName && selectedTasks?.length > 0
                    ? _.tableName === selectedTasks?.[0].tableName
                    : true,
            )
            .map(_ => toTaskReport(_))
            .filter(task => isValidTaskReport(task))
            .map(task => {
                const selected = selectedTasks.some(
                    _ =>
                        _.tableName === task.tableName &&
                        _.columnName === task.columnName,
                );
                const icon = columnToIcon(task?.dataType);
                return {
                    icon,
                    tab: "tasks",
                    group: task.tableName,
                    label: task.columnName,
                    disabled: selected,
                    toggled: selected,
                    checked: selected,
                    onClick: () => {
                        onSelectTableColumn(task, selected);
                    },
                };
            });
    };

    const listEvents = (): IItem[] => {
        const selectedTasks = (selectedTableColumns || [])
            .filter(_ => _.type === "event")
            .map(_ => toEvent(_));
        return ["all", "notifications", ...eventTypes].map(e => {
            const selected = selectedTasks.some(_ => _.eventType === e);
            const icon = columnToIcon("number");
            const groupBy: string[] = [];
            if (e === "notifications") {
                groupBy.push("severity");
            } else if (e === "all") {
                groupBy.push("type");
            }
            return {
                icon,
                tab: "events",
                group: "EVENT TYPES",
                label: e,
                toggled: selected,
                checked: selected,
                disabled: selected,
                onClick: () => {
                    onSelectTableColumn(
                        toEvent({
                            tableName: e,
                            eventType: e,
                            groupBy,
                            type: "event",
                        } as IEventColumn),
                        selected,
                    );
                },
            };
        });
    };

    const items: AnalyticsItem[] = [
        ...listTaskReports(),
        ...listStreams(),
        ...listEvents(),
    ];

    const groups: IPickerGroup<AnalyticsGroup>[] = items
        .filter(_ => _.group !== undefined)
        .map(_ => ({
            id: defined(_.group),
            label: defined(_.group),
        }));

    const tabs: IPickerTab<AnalyticsPickerTabs>[] = [
        { id: "streams", label: "Streams" },
        { id: "events", label: "Events" },
        { id: "tasks", label: "Tasks" },
    ];

    return (
        <Picker
            displayType={displayType || "dropdown"}
            onSelectItem={onSelectItem}
            searchPlaceholder="Search..."
            selectedTab={selectedTab}
            onSelectTab={setSelectedTab}
            noAutoSelectFirst={true}
            tabs={tabs}
            groups={groups}
            items={items}
            style={{
                minWidth: "23rem",
                maxWidth: "23rem",
                minHeight: "30rem",
                maxHeight: "30rem",
            }}
        />
    );
}

const sqlColumnToIcon: Record<
    "string" | "number" | "boolean" | "timestamp" | "null",
    IconName
> = {
    string: "text",
    number: "scatter",
    boolean: "bitset",
    timestamp: "text",
    null: "scatter",
};

const columnToIcon = (
    input?: "string" | "number" | "boolean" | "timestamp" | "null",
): IconName => {
    return sqlColumnToIcon[input || "number"] ?? "scatter";
};

export const isValidTaskReport = (task: ITaskReportColumn): boolean => {
    return ![
        "ID",
        "CREATEDAT",
        "UPDATEDAT",
        "ORGANIZATIONID",
        "TIME",
        "DEVICEID",
        "STREAMNAME",
        "TAGGEDUSERS",
        "TYPE",
        "VIEWED",
        "NOTIFICATIONENABLED",
        "USERID",
        "ANNOTATIONTEMPLATEID",
        "TAGGEDUSERS",
        "PUBLISHEDTO",
        "STREAMTYPE",
        "EDITEDAT",
    ].includes(task.columnName);
};
